/**
 * Service Worker State Types and Events
 *
 * Centralized definitions for Service Worker communication between sw.ts and frontend
 * This file is shared between CacheManager (SW side) and frontend hooks/contexts
 */

/**
 * Type d'interruption détecté lors d'une sync ou d'un fetch intercepté.
 *
 * null = aucun changement, flux normal.
 *
 * Trois cas distincts :
 *
 * - UNAUTHORIZED          : 401 du serveur — session expirée → purge cache + redirect login
 *
 * - DATA_CONTEXT_CHANGED  : DataContext changé (userId, bailleurId ou dataVersion) →
 *                           purge cache + re-sync automatique.
 *                           Causes : changement d'utilisateur, changement de bailleur
 *                           pour un même email, ou reset de la base (restauration, env test).
 *
 * - SW_VERSION_CHANGED    : hash du SW changé (header x-service-worker-version) —
 *                           toute modification du code app change ce hash →
 *                           sync abortée proprement, données stale servies depuis IndexedDB,
 *                           page rechargée automatiquement via flow SW update
 */
export enum ContextChangeType {
    UNAUTHORIZED = 'UNAUTHORIZED',                     // Session expirée (401)
    DATA_CONTEXT_CHANGED = 'DATA_CONTEXT_CHANGED',     // DataContext changé (userId/bailleurId/dataVersion)
    SW_VERSION_CHANGED = 'SW_VERSION_CHANGED'          // Hash SW changé → reload imminent
}

/**
 * Discriminant de tous les events émis par le Service Worker vers le frontend.
 *
 * Phase events (changent AppSWState.phase) :
 * - LOADING: initialize() en cours
 * - SYNCING: sync logements active avec progression disponible
 * - READY: données disponibles et à jour
 * - UNAVAILABLE: données introuvables (jamais synchronisé + erreur)
 * - SW_UPDATING: mise à jour SW en cours → reload imminent
 *
 * Telemetry events (AppInsights uniquement, ne changent pas la phase) :
 * - INITIALIZATION_STARTED, INITIALIZATION_COMPLETED, INITIALIZATION_FAILED
 * - CLEAR_STARTED, CLEAR_COMPLETED
 * - LOGEMENTS_SYNC_STARTED, LOGEMENTS_SYNC_COMPLETED, LOGEMENTS_SYNC_FAILED
 * - DOCUMENTS_SYNC_PROGRESS, DOCUMENTS_SYNC_COMPLETED
 *
 * Flow : CacheManager → message listeners SW → Frontend (via postMessage)
 */
export enum SWEventType {
    // Phase events (changent AppSWState.phase)
    LOADING = 'LOADING',
    SYNCING = 'SYNCING',
    READY = 'READY',
    UNAVAILABLE = 'UNAVAILABLE',
    SW_UPDATING = 'SW_UPDATING',

    // Telemetry events (AppInsights uniquement)
    INITIALIZATION_STARTED = 'INITIALIZATION_STARTED',
    INITIALIZATION_COMPLETED = 'INITIALIZATION_COMPLETED',
    INITIALIZATION_FAILED = 'INITIALIZATION_FAILED',
    CLEAR_STARTED = 'CLEAR_STARTED',
    CLEAR_COMPLETED = 'CLEAR_COMPLETED',
    LOGEMENTS_SYNC_STARTED = 'LOGEMENTS_SYNC_STARTED',
    LOGEMENTS_SYNC_COMPLETED = 'LOGEMENTS_SYNC_COMPLETED',
    LOGEMENTS_SYNC_FAILED = 'LOGEMENTS_SYNC_FAILED',
    DOCUMENTS_SYNC_PROGRESS = 'DOCUMENTS_SYNC_PROGRESS',
    DOCUMENTS_SYNC_COMPLETED = 'DOCUMENTS_SYNC_COMPLETED',
}

/**
 * Types individuels de chaque event SW.
 * Chaque type porte exactement les champs requis pour cet event — pas de champs optionnels.
 */

// Phase events
export type LoadingEvent = { type: SWEventType.LOADING };
export type SyncingEvent = {
    type: SWEventType.SYNCING;
    details: {
        logEntryCreatedDate: string;
        minLogEntryCreatedDate: string;
        itemCount: number;
    }
};
export type ReadyEvent = { type: SWEventType.READY; details: { pendingCount: number } };
export type UnavailableEvent = { type: SWEventType.UNAVAILABLE };
export type SWUpdatingEvent = { type: SWEventType.SW_UPDATING };

// Telemetry events
export type InitializationStartedEvent = { type: SWEventType.INITIALIZATION_STARTED };
export type InitializationCompletedEvent = { type: SWEventType.INITIALIZATION_COMPLETED };
export type InitializationFailedEvent = { type: SWEventType.INITIALIZATION_FAILED; details: { error?: string; reason?: ContextChangeType } };
export type ClearStartedEvent = { type: SWEventType.CLEAR_STARTED; details: { reason: ContextChangeType } };
export type ClearCompletedEvent = { type: SWEventType.CLEAR_COMPLETED; details: { reason: ContextChangeType } };
export type LogementsSyncStartedEvent = { type: SWEventType.LOGEMENTS_SYNC_STARTED };
export type LogementsSyncCompletedEvent = { type: SWEventType.LOGEMENTS_SYNC_COMPLETED };
export type LogementsSyncFailedEvent = { type: SWEventType.LOGEMENTS_SYNC_FAILED; details: { error?: string; reason?: ContextChangeType; stack?: string } };
export type DocumentsSyncProgressEvent = {
    type: SWEventType.DOCUMENTS_SYNC_PROGRESS;
    details: {
        current: number;
        total: number;
    }
};
export type DocumentsSyncCompletedEvent = { type: SWEventType.DOCUMENTS_SYNC_COMPLETED };

/**
 * Union de tous les events que le Service Worker peut émettre vers le frontend.
 */
export type SWStateEvent =
    // Phase events
    | LoadingEvent
    | SyncingEvent
    | ReadyEvent
    | UnavailableEvent
    | SWUpdatingEvent
    // Telemetry events
    | InitializationStartedEvent
    | InitializationCompletedEvent
    | InitializationFailedEvent
    | ClearStartedEvent
    | ClearCompletedEvent
    | LogementsSyncStartedEvent
    | LogementsSyncCompletedEvent
    | LogementsSyncFailedEvent
    | DocumentsSyncProgressEvent
    | DocumentsSyncCompletedEvent;
